import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Table } from './components/Table';
import { composeColumnData } from '../utils/compose-column-data';
import { composeRowData } from '../utils/compose-row-data';
import { FETCH_LEADERBOARD_DATA } from '../../../../store/js/modules/draft-tracker/leaderboard/actions';
import {
    leaderboardDataSelector,
    leaderboardFilterSelector,
    leaderboardNavSelector
} from '../../../../store/js/modules/draft-tracker/leaderboard/selectors';
import { Loader } from '../../../../../../common/react/components/Loader';
import { EmptyState } from '../../../../../../common/react/components/EmptyState';
import { useWindowWidth } from '../../../../../../common/react/hooks/use-window-resize';

export const DraftTrackerLeaderboardTable = ({ year }) => {
    const dispatch = useDispatch();
    const draftData = useSelector((state) =>
        leaderboardDataSelector(state, year)
    );
    const allSelections = draftData?.draftFeeds?.[0]?.selections ?? [];
    const nav = useSelector(leaderboardNavSelector);
    const filter = useSelector(leaderboardFilterSelector);
    const windowWidth = useWindowWidth(500);

    // Fetch data by year
    useEffect(() => {
        dispatch(
            FETCH_LEADERBOARD_DATA.request({
                year
            })
        );
    }, [year]);

    /**
     * Get columns.
     */
    const columns = useMemo(
        () => composeColumnData(windowWidth),
        [windowWidth]
    );

    /**
     * Get rows.
     */
    const data = useMemo(() => {
        let selections = allSelections;

        // filter by round
        if (nav.round !== -1) {
            selections = selections?.filter(
                (item) => item.roundNumber === nav.round
            );
        }

        // filter by club
        if (nav.club !== -1) {
            selections = selections?.filter((item) => item.teamId === nav.club);
        }

        // filter by search query
        if (filter !== '') {
            const queryWords = filter
                .split(' ')
                .map((query) => query.toLowerCase());

            selections = selections.filter((item) =>
                /** look through the query words and if any have a match
                 * against either the given or surname then include in the
                 * filter
                 */
                queryWords.some(
                    (query) =>
                        item.fullName
                            ?.split(' ')[0]
                            ?.toLowerCase()
                            .includes(query) ||
                        item.fullName
                            ?.split(' ')[1]
                            ?.toLowerCase()
                            .includes(query)
                )
            );
        }

        // find the current pick number from the data
        const currentPick = allSelections?.find(
            (item) => !item.fullName
        )?.pickNumber;

        return selections?.map((item) => composeRowData(item, currentPick));
    }, [draftData, nav, filter]);

    if (!draftData) {
        // Loader for waiting for data
        return (
            <div className="draft-tracker-leaderboard__loader">
                <Loader />
            </div>
        );
    } else if (!allSelections.length) {
        // Error handling for when there is no data
        return (
            <div className="draft-tracker-leaderboard__empty-state">
                <EmptyState
                    titleTranslation="label.empty.state.noData.title"
                    summaryTranslation="label.empty.state.noData.summary"
                />
            </div>
        );
    }

    return (
        <Table
            columns={columns}
            data={data}
            modifier="draft-tracker-leaderboard"
        />
    );
};

DraftTrackerLeaderboardTable.propTypes = {
    year: PropTypes.string.isRequired
};
